import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { SystemEntity } from '@erp/shared-types';
import { isFoldCancelledDuplicateLogTask } from '@erp/rpc';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import type {
  ListCashReceiptsInput,
  ListCashReceiptsResponse,
  GetCashReceiptResponse,
  CashReceiptListItem,
  SyncCashReceiptsInput,
  SyncCashReceiptsResponse,
  OverrideCustomerInput,
  OverrideCustomerResponse,
  DeleteCashReceiptInput,
  DeleteCashReceiptResponse,
  CreateCashReceiptInput,
  CreateCashReceiptResponse,
  UpdateCashReceiptInput,
  UpdateCashReceiptResponse,
  UnlinkCashReceiptInput,
  RematchCashReceiptInput,
  SetCashReceiptRemittanceMissingInput,
  GetCashReceiptWorkItemResponse,
  CreateCashReceiptLogTaskInput,
  CreateCashReceiptLogTaskResponse,
  BulkCreateCashReceiptLogTasksInput,
  BulkCreateCashReceiptLogTasksResponse,
  BulkCloseCashReceiptLogTasksInput,
  BulkCloseCashReceiptLogTasksResponse,
  LinkCashReceiptRemittanceInput,
  LinkCashReceiptResponse,
  LinkCashReceiptRemittancesInput,
  LinkCashReceiptRemittancesResponse,
  UnlinkCashReceiptRemittanceInput,
  UnlinkCashReceiptRemittanceResponse,
  CashReceiptActivity,
  AddCashReceiptNoteInput,
  EditCashReceiptNoteInput,
  CashReceiptNoteResponse,
  DeleteCashReceiptNoteInput,
  DeleteCashReceiptNoteResponse,
} from '@erp/rpc';

/**
 * Query hook for the paginated cash receipt review queue.
 * Returns receipts with confidence from AiReasoningAudit.
 */
export function useArCashReceiptsList(filters: ListCashReceiptsInput = {}) {
  const query = useQuery(
    orpc.accountsReceivable.arCashReceipts.list.queryOptions({
      input: filters,
    }),
  );

  return {
    ...query,
    receipts: (query.data?.receipts ?? []) as CashReceiptListItem[],
    pagination: query.data?.pagination,
  } satisfies typeof query & {
    data?: ListCashReceiptsResponse;
    receipts: CashReceiptListItem[];
    pagination: ListCashReceiptsResponse['pagination'] | undefined;
  };
}

/**
 * Query hook for a single cash receipt detail with AI reasoning and linked payment.
 */
export function useArCashReceipt(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCashReceipts.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    receipt: query.data?.receipt ?? null,
  } satisfies typeof query & {
    data?: GetCashReceiptResponse;
    receipt: GetCashReceiptResponse['receipt'] | null;
  };
}

/**
 * Mutation hook to trigger the Plaid sync + cash receipt pipeline for a date range.
 * Invalidates the list cache on success so the review queue refreshes automatically.
 */
export function useSyncCashReceipts() {
  const queryClient = useQueryClient();

  return useMutation<SyncCashReceiptsResponse, Error, SyncCashReceiptsInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.sync(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.queryOptions({
          input: {},
        }).queryKey,
      });
    },
  });
}

/**
 * Mutation hook to override the customer on a cash receipt.
 * Invalidates both the list and the specific receipt detail cache on success.
 */
export function useOverrideCashReceiptCustomer() {
  const queryClient = useQueryClient();

  return useMutation<OverrideCustomerResponse, Error, OverrideCustomerInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.overrideCustomer(
        input,
      );
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.queryOptions({
          input: {},
        }).queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.get.queryOptions({
          input: { id: variables.receiptId },
        }).queryKey,
      });
    },
  });
}

/**
 * Mutation hook to permanently delete a cash receipt. Used as an operator
 * escape hatch for badly-extracted rows. Invalidates the list cache on
 * success so the deleted row disappears from the review queue.
 */
export function useDeleteCashReceipt() {
  const queryClient = useQueryClient();

  return useMutation<DeleteCashReceiptResponse, Error, DeleteCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.delete(input);
    },
    onSuccess: async () => {
      // Use `.key()` (prefix-only) so we invalidate the list regardless of
      // which filters the active query is using. `queryOptions({input:{}}).queryKey`
      // would only match queries called with empty filters.
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
      });
    },
  });
}

function invalidateCashReceiptQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.get.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.getWorkItem.key(),
  });
  // The hand-pick panel reads its own amount and settlement.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.getHandPick.key(),
  });
  // Linking, unlinking and rematching can post or move applications, which
  // the applications panel and invoice balances read separately.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arPaymentApplications.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arInvoices.list.key(),
  });
}

/** Manually create a cash receipt (bank transaction). */
export function useCreateCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<CreateCashReceiptResponse, Error, CreateCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.create(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}

/** Edit a manual-origin cash receipt. */
export function useUpdateCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<UpdateCashReceiptResponse, Error, UpdateCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.update(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}

/**
 * Break a cash-receipt ⇄ payment match: clears the payment link and returns
 * the receipt to needs_review. Prerequisite for editing a reconciled
 * remittance's amount.
 */
export function useUnlinkCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<OverrideCustomerResponse, Error, UnlinkCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.unlink(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}

/** Manually re-run matching for a single cash receipt against waiting remittances. */
export function useRematchCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<OverrideCustomerResponse, Error, RematchCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.rematch(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}

/**
 * Mark (or clear) that a receipt's remittance email never got recorded; the
 * cash metrics dashboard counts marked receipts as missed remittances.
 */
export function useSetCashReceiptRemittanceMissing() {
  const queryClient = useQueryClient();
  return useMutation<
    GetCashReceiptResponse,
    Error,
    SetCashReceiptRemittanceMissingInput
  >({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.setRemittanceMissing(
        input,
      );
    },
    onSuccess: () => {
      invalidateCashReceiptQueries(queryClient);
      void invalidateCashReceiptActivity(queryClient);
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashMetrics.get.key(),
      });
    },
  });
}

/**
 * The full cash-receipt work item: receipt + linked payment, remittances with
 * line items, and candidate payments and remittances while unmatched. One
 * endpoint feeds the slide-in's remittance and candidate sections.
 */
export function useArCashReceiptWorkItem(
  id: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCashReceipts.getWorkItem.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    workItem: query.data ?? null,
  } satisfies typeof query & {
    data?: GetCashReceiptWorkItemResponse;
    workItem: GetCashReceiptWorkItemResponse | null;
  };
}

/**
 * File a receipt's log-cash-receipt task by hand. Idempotent — `created` is
 * false when the receipt already had an open task, which is returned instead
 * of a duplicate.
 */
export function useCreateCashReceiptLogTask() {
  const queryClient = useQueryClient();
  return useMutation<
    CreateCashReceiptLogTaskResponse,
    Error,
    CreateCashReceiptLogTaskInput
  >({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.createLogTask(input);
    },
    // A new task moves the receipt's lifecycle, so refresh the row and
    // detail along with the work item and every task surface.
    onSuccess: () => invalidateLogTaskQueries(queryClient),
  });
}

/**
 * The receipt's current logging task — its most recently created one, open or
 * closed, that a fold did not cancel as a duplicate, which is what the list
 * row shows (see `isFoldCancelledDuplicateLogTask`). Only the log-cash-receipt
 * type anchors to a cash receipt, so the anchor alone identifies it. Disabled
 * for callers who cannot read tasks.
 *
 * The task list cannot exclude those duplicates server-side, so this reads
 * the newest tasks, up to the list's 100-row cap, and skips them here. A
 * receipt gains at most one per receipt folded into it, so a hundred of them
 * stacked on one receipt is not a case worth paging for.
 */
export function useCashReceiptLatestLogTask(receiptId: string) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    ...orpc.tasks.list.queryOptions({
      input: {
        anchorEntityType: SystemEntity.AR_CASH_RECEIPT,
        anchorEntityId: receiptId,
        limit: 100,
        offset: 0,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      },
    }),
    enabled: canReadTasks && receiptId.length > 0,
  });
  return {
    task:
      query.data?.tasks.find(
        (task) => !isFoldCancelledDuplicateLogTask(task),
      ) ?? null,
    isLoading: canReadTasks && query.isLoading,
  };
}

/**
 * Refresh every surface a log-task change shows up on: the list's task
 * column, the open receipt's work panel, and the task queue itself.
 */
function invalidateLogTaskQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  invalidateCashReceiptQueries(queryClient);
  invalidateTaskQueries(queryClient);
}

/** File the logging task for every selected receipt that has none open. */
export function useBulkCreateCashReceiptLogTasks() {
  const queryClient = useQueryClient();
  return useMutation<
    BulkCreateCashReceiptLogTasksResponse,
    Error,
    BulkCreateCashReceiptLogTasksInput
  >({
    mutationFn: (input) =>
      rpcClient.accountsReceivable.arCashReceipts.bulkCreateLogTasks(input),
    onSuccess: () => invalidateLogTaskQueries(queryClient),
  });
}

/** Close the open logging task of every selected receipt with one outcome. */
export function useBulkCloseCashReceiptLogTasks() {
  const queryClient = useQueryClient();
  return useMutation<
    BulkCloseCashReceiptLogTasksResponse,
    Error,
    BulkCloseCashReceiptLogTasksInput
  >({
    mutationFn: (input) =>
      rpcClient.accountsReceivable.arCashReceipts.bulkCloseLogTasks(input),
    // Partial failures still closed the rest, so refresh either way.
    onSettled: () => invalidateLogTaskQueries(queryClient),
  });
}

/**
 * Hand-pick link: attach a remittance advice to a receipt. The response flags
 * an amount mismatch (allowed, but the UI should warn).
 */
export function useLinkCashReceiptRemittance() {
  const queryClient = useQueryClient();
  return useMutation<
    LinkCashReceiptResponse,
    Error,
    LinkCashReceiptRemittanceInput
  >({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.linkRemittance(input);
    },
    onSuccess: () => {
      invalidateCashReceiptQueries(queryClient);
      // The advice is now linked to this receipt.
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arRemittances.list.key(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Hand-pick several remittances (GEN-3106)
// ---------------------------------------------------------------------------

/** The multi-remittance hand-pick panel: links, settlement, candidates. */
export function useArCashReceiptHandPick(
  id: string | undefined,
  search: string,
  enabled = true,
) {
  const trimmed = search.trim();
  return useQuery({
    ...orpc.accountsReceivable.arCashReceipts.getHandPick.queryOptions({
      input: id ? { id, ...(trimmed && { search: trimmed }) } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
    placeholderData: (previous) => previous,
  });
}

/** Everything a remittance link change can move on screen. */
function invalidateRemittanceLinkQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  invalidateCashReceiptQueries(queryClient);
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.listActivity.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arRemittances.list.key(),
  });
}

/** Link several picked remittances to one receipt in one save. */
export function useLinkCashReceiptRemittances() {
  const queryClient = useQueryClient();
  return useMutation<
    LinkCashReceiptRemittancesResponse,
    Error,
    LinkCashReceiptRemittancesInput
  >({
    mutationFn: (input) =>
      rpcClient.accountsReceivable.arCashReceipts.linkRemittances(input),
    onSuccess: () => invalidateRemittanceLinkQueries(queryClient),
    // A refusal (someone linked in the meantime, or the total now exceeds the
    // receipt) means the panel's view is stale: reload it, so the running sum
    // shows the real state and an over-link asks for confirmation.
    onError: () => invalidateRemittanceLinkQueries(queryClient),
  });
}

/** Close one hand-picked remittance link with a reason. */
export function useUnlinkCashReceiptRemittance() {
  const queryClient = useQueryClient();
  return useMutation<
    UnlinkCashReceiptRemittanceResponse,
    Error,
    UnlinkCashReceiptRemittanceInput
  >({
    mutationFn: (input) =>
      rpcClient.accountsReceivable.arCashReceipts.unlinkRemittance(input),
    onSuccess: () => invalidateRemittanceLinkQueries(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Activity timeline
// ---------------------------------------------------------------------------

/** One receipt's activity feed — lifecycle entries plus user notes. */
export function useArCashReceiptActivity(
  receiptId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCashReceipts.listActivity.queryOptions({
      input: receiptId ? { receiptId } : skipToken,
    }),
    enabled: Boolean(receiptId) && enabled,
  });

  return {
    ...query,
    activities: (query.data?.activities ?? []) as CashReceiptActivity[],
  };
}

/**
 * Invalidates one receipt's feed. Notes never change the receipt row itself,
 * so this deliberately does not touch the list/detail queries.
 *
 * Returns the promise so each mutation's `onSuccess` waits on the refetch:
 * otherwise `isPending` drops while the stale feed is still rendered, and the
 * delete button on a row the server has already removed comes back to life
 * long enough for a second click to fail with "Note not found".
 */
function invalidateCashReceiptActivity(
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.listActivity.key(),
  });
}

export function useAddCashReceiptNote() {
  const queryClient = useQueryClient();
  return useMutation<CashReceiptNoteResponse, Error, AddCashReceiptNoteInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.addNote(input);
    },
    onSuccess: () => invalidateCashReceiptActivity(queryClient),
  });
}

export function useEditCashReceiptNote() {
  const queryClient = useQueryClient();
  return useMutation<CashReceiptNoteResponse, Error, EditCashReceiptNoteInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.editNote(input);
    },
    onSuccess: () => invalidateCashReceiptActivity(queryClient),
  });
}

export function useDeleteCashReceiptNote() {
  const queryClient = useQueryClient();
  return useMutation<
    DeleteCashReceiptNoteResponse,
    Error,
    DeleteCashReceiptNoteInput
  >({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.deleteNote(input);
    },
    onSuccess: () => invalidateCashReceiptActivity(queryClient),
  });
}
